import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPropertyById } from '../services/api';
import './PropertyDetail.css';

const PropertyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const [quoteForm, setQuoteForm] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    useEffect(() => {
        const loadProperty = async () => {
            try {
                setLoading(true);
                const data = await getPropertyById(id);
                setProperty(data);
            } catch (error) {
                console.error('Error loading property:', error);
                alert('Property not found!');
                navigate('/');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadProperty();
        }
    }, [id, navigate]);

    const handleQuoteChange = (e) => {
        setQuoteForm({
            ...quoteForm,
            [e.target.name]: e.target.value
        });
    };

    const handleQuoteSubmit = (e) => {
        e.preventDefault();
        // Here you can send the quote request to backend
        alert(`Thank you ${quoteForm.name}! Your quote request has been submitted. We will contact you soon at ${quoteForm.email} or ${quoteForm.phone}.`);
        setQuoteForm({
            name: '',
            email: '',
            phone: '',
            message: ''
        });
        setShowQuoteForm(false);
    };

    if (loading) {
        return (
            <div className="property-detail-loading">
                <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading property details...</p>
                </div>
            </div>
        );
    }

    if (!property) {
        return (
            <div className="property-detail-error">
                <h2>Property Not Found</h2>
                <Link to="/" className="back-home-btn">Back to Home</Link>
            </div>
        );
    }

    return (
        <div className="property-detail-page">
            <div className="property-detail-header">
                <div className="container">
                    <Link to="/" className="back-link">
                        <i className="fas fa-arrow-left"></i> Back to Properties
                    </Link>
                </div>
            </div>

            <div className="property-detail-content">
                <div className="container">
                    <div className="property-detail-grid">
                        <div className="property-detail-left">
                            <div className="property-detail-image">
                                {property.imageUrl || property.image_url ? (
                                    <img 
                                        src={property.imageUrl || property.image_url} 
                                        alt={property.title}
                                        className="property-image-large-img"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div className="property-image-large" style={{ display: property.imageUrl || property.image_url ? 'none' : 'flex' }}>
                                    {property.image}
                                </div>
                                <span className="property-badge-large">{property.badge}</span>
                            </div>

                            <div className="property-details-section">
                                <h2>Property Details</h2>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <i className="fas fa-bed"></i>
                                        <div>
                                            <span className="detail-label">Bedrooms</span>
                                            <span className="detail-value">{property.bedrooms}</span>
                                        </div>
                                    </div>
                                    <div className="detail-item">
                                        <i className="fas fa-bath"></i>
                                        <div>
                                            <span className="detail-label">Bathrooms</span>
                                            <span className="detail-value">{property.bathrooms}</span>
                                        </div>
                                    </div>
                                    <div className="detail-item">
                                        <i className="fas fa-ruler-combined"></i>
                                        <div>
                                            <span className="detail-label">Area</span>
                                            <span className="detail-value">{property.area} sqft</span>
                                        </div>
                                    </div>
                                    <div className="detail-item">
                                        <i className="fas fa-home"></i>
                                        <div>
                                            <span className="detail-label">Type</span>
                                            <span className="detail-value">{property.type.charAt(0).toUpperCase() + property.type.slice(1)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="property-description">
                                <h3>About This Property</h3>
                                <p>
                                    This stunning {property.type} located in {property.location} offers the perfect blend of luxury and comfort. 
                                    With {property.bedrooms} bedrooms and {property.bathrooms} bathrooms, this property provides ample space for 
                                    modern living. The {property.area} sqft area ensures you have plenty of room to create your dream home.
                                </p>
                                <p>
                                    Located in one of Dubai's most prestigious areas, this property offers easy access to shopping, dining, 
                                    and entertainment. Don't miss this opportunity to own a piece of luxury real estate in the heart of Dubai.
                                </p>
                            </div>
                        </div>

                        <div className="property-detail-right">
                            <div className="property-info-card">
                                <div className="property-price-large">
                                    ${property.price.toLocaleString()}
                                </div>
                                <h1 className="property-title-large">{property.title}</h1>
                                <div className="property-location-large">
                                    <i className="fas fa-map-marker-alt"></i>
                                    {property.location}
                                </div>

                                <div className="property-actions">
                                    <button 
                                        onClick={() => setShowQuoteForm(!showQuoteForm)}
                                        className="request-quote-btn"
                                    >
                                        <i className="fas fa-file-invoice-dollar"></i>
                                        {showQuoteForm ? 'Cancel Request' : 'Request a Quote'}
                                    </button>
                                    <a href="tel:+971585110336" className="call-btn">
                                        <i className="fas fa-phone"></i> Call Now
                                    </a>
                                </div>

                                {showQuoteForm && (
                                    <div className="quote-form-container">
                                        <h3>Request a Quote</h3>
                                        <p className="quote-form-subtitle">Fill out the form below and we'll get back to you with a personalized quote.</p>
                                        <form onSubmit={handleQuoteSubmit} className="quote-form">
                                            <div className="form-group">
                                                <label>Full Name *</label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={quoteForm.name}
                                                    onChange={handleQuoteChange}
                                                    placeholder="Enter your full name"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Email Address *</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={quoteForm.email}
                                                    onChange={handleQuoteChange}
                                                    placeholder="Enter your email"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Phone Number *</label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={quoteForm.phone}
                                                    onChange={handleQuoteChange}
                                                    placeholder="Enter your phone number"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Message (Optional)</label>
                                                <textarea
                                                    name="message"
                                                    value={quoteForm.message}
                                                    onChange={handleQuoteChange}
                                                    placeholder="Any specific questions or requirements?"
                                                    rows="4"
                                                ></textarea>
                                            </div>
                                            <button type="submit" className="submit-quote-btn">
                                                <i className="fas fa-paper-plane"></i> Submit Request
                                            </button>
                                        </form>
                                    </div>
                                )}

                                <div className="contact-info-box">
                                    <h4>Contact Information</h4>
                                    <div className="contact-info-item">
                                        <i className="fas fa-phone"></i>
                                        <div>
                                            <a href="tel:+971585110336">+971 58 511 0336</a>
                                            <a href="tel:+971558352452">+971 55 835 2452</a>
                                        </div>
                                    </div>
                                    <div className="contact-info-item">
                                        <i className="fas fa-envelope"></i>
                                        <div>
                                            <a href="mailto:mansoorsuhaib983@yahoo.com">mansoorsuhaib983@yahoo.com</a>
                                        </div>
                                    </div>
                                    <div className="contact-info-item">
                                        <i className="fas fa-map-marker-alt"></i>
                                        <div>
                                            <span>Opus Tower, Business Bay, Dubai</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyDetail;
